import map from "lodash/map";
import React, { FunctionComponent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ReactComponent as ArrowLeftIcon } from "@Icons/arrow-left.svg";
import { Upsell, UpsellIdentifiers } from "@Types/upsells";
import { getFormattedTotalPrice, getTotalPrice, isSelected } from "@Utils/upsells";
import "./style.scss";
import UpsellItem from "./UpsellItem";

interface Props {
  hideUpsells: () => void;
  onSelectUpsell: (selectedUpsells: UpsellIdentifiers) => void;
  onSubmit: () => void;
  onSubmitWithoutUpsell: () => void;
  onUnselectUpsell: (selectedUpsells: UpsellIdentifiers) => void;
  selectedUpsellsTags: UpsellIdentifiers[];
  upsells: Upsell[];
}

const UpsellsModal: FunctionComponent<Props> = ({
  hideUpsells,
  onSelectUpsell,
  onSubmit,
  onSubmitWithoutUpsell,
  onUnselectUpsell,
  selectedUpsellsTags,
  upsells,
}) => {
  const [hoveredUpsellImage, setHoveredUpsellImage] = useState<string | null>(null);
  const intl = useIntl();

  const handleToggleHover = (imageURL: string) => {
    hoveredUpsellImage === imageURL ? setHoveredUpsellImage(null) : setHoveredUpsellImage(imageURL);
  };

  const getImageUrl = (): string => {
    if (hoveredUpsellImage) {
      return hoveredUpsellImage;
    } else if (selectedUpsellsTags.length > 0) {
      const lastSelectedUpsell = upsells.find((upsell) => {
        return (
          upsell.tag === selectedUpsellsTags[selectedUpsellsTags.length - 1].tag &&
          upsell.value === selectedUpsellsTags[selectedUpsellsTags.length - 1].value
        );
      });
      return lastSelectedUpsell ? lastSelectedUpsell.imageUrl : upsells[0].imageUrl;
    } else {
      return upsells[0].imageUrl;
    }
  };

  return (
    <div className="UpsellsModal">
      <div className="UpsellsModal-content">
        <div className="UpsellsModal-back" onClick={() => hideUpsells()}>
          <ArrowLeftIcon />
          <span>
            <FormattedMessage id={"cheerz_UpsellsModal_back"} />
          </span>
        </div>
        <div className="UpsellsModal-image" style={{ backgroundImage: `url(${getImageUrl()})` }} />
        <div className="UpsellsModal-form">
          <h2 className="UpsellsModal-title">
            <FormattedMessage id={"cheerz_UpsellsModal_title"} />
          </h2>
          <div className="UpsellsModal-options">
            {map(upsells, (upsell) => {
              return (
                <UpsellItem
                  key={`${upsell.tag}-${upsell.value}`}
                  isSelected={isSelected(upsell.tag, upsell.value, selectedUpsellsTags)}
                  onSelectChange={() =>
                    isSelected(upsell.tag, upsell.value, selectedUpsellsTags)
                      ? onUnselectUpsell(upsell)
                      : onSelectUpsell(upsell)
                  }
                  onToggleHover={() => handleToggleHover(upsell.imageUrl)}
                  upsell={{
                    description: upsell.description,
                    price: upsell.price,
                    title: upsell.title,
                  }}
                />
              );
            })}
          </div>
          <div className="UpsellsModal-buttons">
            <button
              className="UpsellsModal-button-with-upsells"
              onClick={onSubmit}
              disabled={selectedUpsellsTags.length === 0}
            >
              {getTotalPrice(upsells, selectedUpsellsTags) === 0 ? (
                <FormattedMessage id={"cheerz_UpsellsModal_button__default"} />
              ) : (
                <FormattedMessage
                  id={"cheerz_UpsellsModal_button__with_upsell"}
                  values={{
                    totalPrice: getFormattedTotalPrice(upsells, selectedUpsellsTags, intl),
                  }}
                />
              )}
            </button>
            <div onClick={onSubmitWithoutUpsell} className="UpsellsModal-link-without-upsell">
              <FormattedMessage id={"cheerz_UpsellsModal_button__without_upsell"} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpsellsModal;
